import { useCallback, useEffect, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthorization } from '@/auth/hooks/use-authorization';
import { useSwitchAccount } from '@/auth/hooks/use-switch-account.mutation';
import { useCurrentUser } from '@/user/use-current-user.query';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { QueryKey } from '@/config/query-client';
import { fetchWhoAmI } from '@/api/v4/auth.api';

interface ValidationData {
  isValid: boolean;
  accountId?: string | null;
  accountName?: string | null;
}

interface UseAccountSwitchOnLinkValidationParams {
  noAccessBehavior?: 'notFound' | 'redirectToFeed';
  onSwitchComplete?: (previousAccountName: string) => void;
  validationData?: ValidationData;
}

interface UseAccountSwitchOnLinkValidationReturn {
  handleAccountSwitch: () => void;
  isSwitching: boolean;
  shouldSwitch: boolean;
}

const RETRY_DELAY_MS = 300;
const MAX_RETRIES = 3;

export const useAccountSwitchOnLinkValidation = ({
  noAccessBehavior = 'notFound',
  onSwitchComplete,
  validationData,
}: UseAccountSwitchOnLinkValidationParams): UseAccountSwitchOnLinkValidationReturn => {
  const { isUserAuthorized } = useAuthorization();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { mutateAsync: switchAccount } = useSwitchAccount({
    skipHomeNavigation: true,
  });
  const navigateTo = useNavigateTo();
  const [isSwitching, setIsSwitching] = useState(false);
  const [shouldSwitch, setShouldSwitch] = useState(false);
  const hasEvaluatedRef = useRef(false);
  const evaluatedAccountIdRef = useRef<string | null>(null);

  const currentAccountId = currentUser.data?.account.id;
  const userAccounts = currentUser.data?.userAccounts;

  const handleNoAccess = useCallback(() => {
    if (noAccessBehavior === 'redirectToFeed') {
      navigateTo.feed();
      return;
    }

    navigateTo.notFound();
  }, [navigateTo, noAccessBehavior]);

  useEffect(() => {
    if (isSwitching) {
      return;
    }

    if (!validationData?.isValid || !isUserAuthorized) {
      setShouldSwitch(false);
      hasEvaluatedRef.current = false;
      evaluatedAccountIdRef.current = null;
      return;
    }

    if (!currentUser.data) {
      setShouldSwitch(false);
      hasEvaluatedRef.current = false;
      evaluatedAccountIdRef.current = null;
      return;
    }

    if (!validationData.accountId) {
      handleNoAccess();
      setShouldSwitch(false);
      hasEvaluatedRef.current = false;
      evaluatedAccountIdRef.current = null;
      return;
    }

    const { accountId } = validationData;

    if (
      hasEvaluatedRef.current &&
      evaluatedAccountIdRef.current === accountId &&
      currentAccountId === evaluatedAccountIdRef.current
    ) {
      return;
    }

    if (currentAccountId === accountId) {
      setShouldSwitch(false);
      hasEvaluatedRef.current = true;
      evaluatedAccountIdRef.current = accountId;
      return;
    }

    if (!userAccounts || userAccounts.length === 0) {
      setShouldSwitch(false);
      hasEvaluatedRef.current = true;
      evaluatedAccountIdRef.current = accountId;
      return;
    }

    const targetAccount = userAccounts.find(ua => ua.account.id === accountId);

    if (!targetAccount) {
      handleNoAccess();
      setShouldSwitch(false);
      hasEvaluatedRef.current = true;
      evaluatedAccountIdRef.current = accountId;
      return;
    }

    if (
      !hasEvaluatedRef.current ||
      evaluatedAccountIdRef.current !== accountId
    ) {
      setShouldSwitch(true);
      hasEvaluatedRef.current = true;
      evaluatedAccountIdRef.current = accountId;
    }
  }, [
    currentAccountId,
    handleNoAccess,
    isSwitching,
    isUserAuthorized,
    currentUser.data,
    userAccounts,
    validationData?.accountId,
    validationData?.isValid,
  ]);

  const handleAccountSwitch = useCallback(() => {
    if (!validationData?.accountId || !currentUser.data || isSwitching) {
      return;
    }

    const targetAccount = currentUser.data.userAccounts.find(
      ua => ua.account.id === validationData.accountId,
    );

    if (!targetAccount) {
      handleNoAccess();
      return;
    }

    const previousAccountName = currentUser.data.account.name || '';
    setIsSwitching(true);
    setShouldSwitch(false);
    hasEvaluatedRef.current = false;
    evaluatedAccountIdRef.current = null;

    void switchAccount(targetAccount.userInAccountId)
      .then(async () => {
        await new Promise<void>(resolve => setTimeout(resolve, RETRY_DELAY_MS));

        let retries = MAX_RETRIES;
        let updatedUser: Awaited<ReturnType<typeof fetchWhoAmI>> | null = null;

        while (retries > 0) {
          try {
            updatedUser = await fetchWhoAmI();
            queryClient.setQueryData([QueryKey.WhoAmI], updatedUser);

            if (updatedUser?.account?.id === validationData.accountId) {
              break;
            }

            if (retries > 1) {
              await new Promise<void>(resolve =>
                setTimeout(resolve, RETRY_DELAY_MS),
              );
            }
          } catch {
            if (retries > 1) {
              await new Promise<void>(resolve =>
                setTimeout(resolve, RETRY_DELAY_MS),
              );
            }
          }

          retries--;
        }

        await new Promise<void>(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      })
      .then(() => {
        setIsSwitching(false);
        onSwitchComplete?.(previousAccountName);
      })
      .catch(() => {
        setIsSwitching(false);
        hasEvaluatedRef.current = false;
        evaluatedAccountIdRef.current = null;
      });
  }, [
    currentUser.data,
    handleNoAccess,
    isSwitching,
    onSwitchComplete,
    queryClient,
    switchAccount,
    validationData?.accountId,
  ]);

  return {
    handleAccountSwitch,
    isSwitching,
    shouldSwitch,
  };
};
